import { type VariantProps } from "class-variance-authority";
import { type businessUnitUsersQuery } from "../api/queries";
import { type UserFilters } from "./Filters";
import { type badgeVariants } from "@/components/ui/badge";
import { type ResultOf } from "@/graphql";
import { removeNothings } from "@/lib/utils/arrays";

type BusinessUnitUser = NonNullable<ResultOf<typeof businessUnitUsersQuery>["businessUnitUsers"]>[0];
export type User = NonNullable<BusinessUnitUser>;

export type UserRole = "admin" | "user";

export const USER_ROLE_CONFIG = {
  admin: {
    label: "Admin",
    badgeVariant: "confirm" as NonNullable<VariantProps<typeof badgeVariants>["variant"]>,
  },
  user: {
    label: "User",
    badgeVariant: "secondary" as NonNullable<VariantProps<typeof badgeVariants>["variant"]>,
  },
} as const;

/**
 * Removes empty values from filter object for URL navigation.
 * Filters out empty arrays, empty strings, undefined, and false values.
 */
export function cleanFilters(filters: UserFilters) {
  const entries = Object.entries(filters);
  const cleanedEntries = removeNothings(
    entries.map(([key, value]) => {
      if (Array.isArray(value) && value.length === 0) return null;
      if (typeof value === "string" && value.length === 0) return null;
      if (value === undefined || value === false) return null;
      return [key, value] as const;
    }),
  );

  return Object.fromEntries(cleanedEntries);
}

/**
 * Checks if user matches search term across name, email, and job title.
 */
export function matchesSearchFilter(user: User, searchTerm?: string): boolean {
  if (!searchTerm) return true;

  const lowerSearchTerm = searchTerm.toLowerCase();
  const fullName = `${user.user.firstName} ${user.user.lastName}`.toLowerCase();
  const email = user.user.email?.toLowerCase() || "";
  const jobTitle = user.user.jobTitle?.toLowerCase() || "";

  return fullName.includes(lowerSearchTerm) || email.includes(lowerSearchTerm) || jobTitle.includes(lowerSearchTerm);
}

export function matchesStatusFilter(user: User, status?: string): boolean {
  if (!status) return true;
  return user.status === status;
}

export function getUserRole(user: User): UserRole {
  return Boolean(user.user.isStaff) ? "admin" : "user";
}

export function getRoleLabel(role: UserRole): string {
  return USER_ROLE_CONFIG[role].label;
}

export function getRoleBadgeVariant(role: UserRole): NonNullable<VariantProps<typeof badgeVariants>["variant"]> {
  return USER_ROLE_CONFIG[role].badgeVariant;
}

export function matchesRoleFilter(user: User, role?: string): boolean {
  if (!role) return true;
  return user.role?.name === role;
}

export function matchesOwnerFilter(user: User, showOwnersOnly?: boolean): boolean {
  if (!showOwnersOnly) return true;
  return Boolean(user.isOrganisationOwner);
}

export function isWithinLast30Days(date: Date): boolean {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  return date >= thirtyDaysAgo;
}

/**
 * Checks if user matches last login filter criteria.
 * - "never": user has never logged in
 * - "recent": user logged in within last 30 days
 * - "older": user logged in more than 30 days ago
 */
export function matchesLastLoginFilter(user: User, lastLoginFilter?: "all" | "never" | "recent" | "older"): boolean {
  if (!lastLoginFilter || lastLoginFilter === "all") return true;

  const lastLogin = user.user.lastLogin;

  switch (lastLoginFilter) {
    case "never":
      return !lastLogin;
    case "recent":
      return !!lastLogin && isWithinLast30Days(new Date(lastLogin));
    case "older":
      return !!lastLogin && !isWithinLast30Days(new Date(lastLogin));
    default:
      return true;
  }
}

/**
 * Applies all active filters to the user list.
 */
export function filterUsers(users: User[], filters: UserFilters): User[] {
  return users.filter(Boolean).filter((user) => {
    return (
      matchesSearchFilter(user, filters.search) &&
      matchesStatusFilter(user, filters.status) &&
      matchesRoleFilter(user, filters.role) &&
      matchesOwnerFilter(user, filters.showOwnersOnly) &&
      matchesLastLoginFilter(user, filters.lastLoginFilter)
    );
  });
}
